import React, { useState, useEffect, useRef } from 'react';
import MainHeader from '../components/MainHeader';
import {
  uploadStockFiles, uploadConsumptionFiles,
  fetchUploadedFiles, clearUploads, runImport
} from '../data/api';

const card = {
  background: '#fff',
  borderRadius: 12,
  padding: '20px 24px',
  boxShadow: '0 2px 12px rgba(0,0,0,.06)',
  marginBottom: 18,
};

const btn = (color = '#2563eb') => ({
  background: color,
  color: '#fff',
  border: 0,
  borderRadius: 8,
  padding: '10px 20px',
  fontWeight: 600,
  fontSize: '.88rem',
  cursor: 'pointer',
});

const badge = (bg, fg) => ({
  background: bg, color: fg,
  borderRadius: 20, padding: '3px 10px',
  fontSize: '.7rem', fontWeight: 700,
  display: 'inline-block',
});

export default function AdminUpload() {
  const [uploaded, setUploaded] = useState([]);
  const [stockStatus, setStockStatus] = useState('');
  const [consStatus, setConsStatus] = useState('');
  const [stockProgress, setStockProgress] = useState(0);
  const [consProgress, setConsProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);
  const stockInputRef = useRef(null);
  const consInputRef = useRef(null);

  const refreshUploaded = () => {
    fetchUploadedFiles()
      .then(r => setUploaded(r.data.files || []))
      .catch(() => setUploaded([]));
  };

  useEffect(() => { refreshUploaded(); }, []);

  // Filter CSV files from a folder selection
  const pickCsvFiles = (fileList) => {
    return Array.from(fileList).filter(f => f.name.toLowerCase().endsWith('.csv'));
  };

  const handleStockUpload = async (e) => {
    const files = pickCsvFiles(e.target.files);
    if (files.length === 0) { setStockStatus('No CSV files found in folder'); return; }

    // Filter to only stock*.csv
    const stockFiles = files.filter(f => /^stock\d*\.csv$/i.test(f.name));
    if (stockFiles.length === 0) {
      setStockStatus('No stock*.csv files found in folder');
      return;
    }

    const formData = new FormData();
    stockFiles.forEach(f => formData.append('files', f));

    setStockStatus(`Uploading ${stockFiles.length} stock files...`);
    setStockProgress(0);

    try {
      const res = await uploadStockFiles(formData, (e) => {
        if (e.total) setStockProgress(Math.round((e.loaded * 100) / e.total));
      });
      setStockStatus(`✅ Uploaded ${res.data.count} files successfully`);
      refreshUploaded();
    } catch (err) {
      setStockStatus(`❌ Upload failed: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleConsumptionUpload = async (e) => {
    const files = pickCsvFiles(e.target.files);
    if (files.length === 0) { setConsStatus('No CSV files found in folder'); return; }

    // Filter to Consumption and pipeline files
    const consFiles = files.filter(f => {
      const n = f.name.toLowerCase();
      return n.includes('consumption') || n.includes('pipeline');
    });
    if (consFiles.length === 0) {
      setConsStatus('No Consumption*.csv or pipeline.csv files found in folder');
      return;
    }

    const formData = new FormData();
    consFiles.forEach(f => formData.append('files', f));

    setConsStatus(`Uploading ${consFiles.length} consumption files...`);
    setConsProgress(0);

    try {
      const res = await uploadConsumptionFiles(formData, (e) => {
        if (e.total) setConsProgress(Math.round((e.loaded * 100) / e.total));
      });
      setConsStatus(`✅ Uploaded ${res.data.count} files successfully`);
      refreshUploaded();
    } catch (err) {
      setConsStatus(`❌ Upload failed: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear all uploaded files from staging area?')) return;
    try {
      await clearUploads();
      refreshUploaded();
      setStockStatus(''); setConsStatus('');
      setStockProgress(0); setConsProgress(0);
    } catch (err) {
      alert('Failed to clear: ' + err.message);
    }
  };

  const handleImport = async () => {
    if (!window.confirm(
      'This will REPLACE ALL existing dashboard data with the uploaded files. Continue?'
    )) return;

    setImporting(true);
    setImportResult(null);
    setImportError(null);
    try {
      const res = await runImport();
      setImportResult(res.data);
    } catch (err) {
      setImportError(err.response?.data || { error: err.message });
    } finally {
      setImporting(false);
    }
  };

  const stockFiles = uploaded.filter(f => /^stock/i.test(f.name));
  const consFiles = uploaded.filter(f => /consumption/i.test(f.name));
  const pipeFiles = uploaded.filter(f => /pipeline/i.test(f.name));

  const canImport = stockFiles.length > 0 && consFiles.length > 0 && !importing;

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>
      <MainHeader />

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px' }}>
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#1e293b' }}>
            📦 Data Upload & Import
          </h2>
          <p style={{ color: '#64748b', fontSize: '.85rem', margin: '4px 0 0' }}>
            Upload EDL stock & consumption CSV folders. Importing will <strong>replace</strong> all existing dashboard data.
          </p>
        </div>

        {/* Upload Stock Card */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b' }}>1️⃣ Stock Data Folder</h3>
              <p style={{ color: '#64748b', fontSize: '.78rem', margin: '4px 0 0' }}>
                Select the folder containing <code>stock1.csv ... stock9.csv</code>
              </p>
            </div>
            <span style={badge('#dbeafe', '#1d4ed8')}>{stockFiles.length} uploaded</span>
          </div>

          <input
            ref={stockInputRef}
            type="file"
            webkitdirectory=""
            directory=""
            multiple
            style={{ display: 'none' }}
            onChange={handleStockUpload}
          />
          <button style={btn('#2563eb')} onClick={() => stockInputRef.current?.click()}>
            📁 Select Stock Folder
          </button>

          {stockStatus && (
            <div style={{ marginTop: 10, fontSize: '.82rem', color: stockStatus.startsWith('❌') ? '#b91c1c' : '#047857' }}>
              {stockStatus}
            </div>
          )}
          {stockProgress > 0 && stockProgress < 100 && (
            <div style={{ marginTop: 8, height: 6, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${stockProgress}%`, height: '100%', background: '#2563eb' }} />
            </div>
          )}
        </div>

        {/* Upload Consumption Card */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b' }}>2️⃣ Consumption & Pipeline Folder</h3>
              <p style={{ color: '#64748b', fontSize: '.78rem', margin: '4px 0 0' }}>
                Select the folder containing <code>Consumption_edl.csv</code> and <code>pipeline.csv</code>
              </p>
            </div>
            <span style={badge('#dcfce7', '#15803d')}>
              {consFiles.length + pipeFiles.length} uploaded
            </span>
          </div>

          <input
            ref={consInputRef}
            type="file"
            webkitdirectory=""
            directory=""
            multiple
            style={{ display: 'none' }}
            onChange={handleConsumptionUpload}
          />
          <button style={btn('#059669')} onClick={() => consInputRef.current?.click()}>
            📁 Select Consumption Folder
          </button>

          {consStatus && (
            <div style={{ marginTop: 10, fontSize: '.82rem', color: consStatus.startsWith('❌') ? '#b91c1c' : '#047857' }}>
              {consStatus}
            </div>
          )}
          {consProgress > 0 && consProgress < 100 && (
            <div style={{ marginTop: 8, height: 6, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${consProgress}%`, height: '100%', background: '#059669' }} />
            </div>
          )}
        </div>

        {/* Uploaded files preview */}
        {uploaded.length > 0 && (
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: '.95rem', color: '#1e293b' }}>
                📋 Staged Files ({uploaded.length})
              </h3>
              <button
                onClick={handleClear}
                style={{ ...btn('#e11d48'), padding: '6px 14px', fontSize: '.78rem' }}
              >
                🗑️ Clear All
              </button>
            </div>
            <div style={{ maxHeight: 200, overflow: 'auto', fontSize: '.78rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                    <th style={{ padding: '6px 10px', color: '#64748b', fontWeight: 600 }}>Name</th>
                    <th style={{ padding: '6px 10px', color: '#64748b', fontWeight: 600 }}>Size</th>
                    <th style={{ padding: '6px 10px', color: '#64748b', fontWeight: 600 }}>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {uploaded.map(f => {
                    const kind = /^stock/i.test(f.name) ? 'Stock'
                      : /consumption/i.test(f.name) ? 'Consumption'
                      : /pipeline/i.test(f.name) ? 'Pipeline' : 'Other';
                    const color = kind === 'Stock' ? '#2563eb'
                      : kind === 'Consumption' ? '#059669'
                      : kind === 'Pipeline' ? '#7c3aed' : '#64748b';
                    return (
                      <tr key={f.name} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '6px 10px' }}>{f.name}</td>
                        <td style={{ padding: '6px 10px', color: '#64748b' }}>
                          {(f.size / 1024).toFixed(1)} KB
                        </td>
                        <td style={{ padding: '6px 10px' }}>
                          <span style={{ color, fontWeight: 600 }}>{kind}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Import Button */}
        <div style={card}>
          <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b' }}>3️⃣ Import & Update Dashboard</h3>
          <p style={{ color: '#64748b', fontSize: '.78rem', margin: '4px 0 12px' }}>
            This will run the import script against the staged files and <strong>replace</strong> all existing data in the dashboard tables.
          </p>
          <button
            onClick={handleImport}
            disabled={!canImport}
            style={{
              ...btn(canImport ? '#dc2626' : '#94a3b8'),
              padding: '12px 28px',
              cursor: canImport ? 'pointer' : 'not-allowed',
            }}
          >
            {importing ? '⏳ Importing... (may take 1-2 minutes)' : '🚀 Import & Replace Data'}
          </button>

          {!canImport && !importing && (
            <div style={{ marginTop: 10, fontSize: '.78rem', color: '#b45309' }}>
              ⚠️ Upload both stock and consumption files before importing.
            </div>
          )}

          {importResult && (
            <div style={{
              marginTop: 14, padding: 12, background: '#dcfce7',
              border: '1px solid #86efac', borderRadius: 8
            }}>
              <div style={{ fontWeight: 700, color: '#15803d', marginBottom: 6 }}>
                ✅ Import Complete — Dashboard Updated
              </div>
              <pre style={{
                maxHeight: 300, overflow: 'auto', fontSize: '.72rem',
                background: '#fff', padding: 10, borderRadius: 6, margin: 0,
                whiteSpace: 'pre-wrap'
              }}>
                {importResult.output}
              </pre>
              <button
                onClick={() => window.location.href = '/heatmap'}
                style={{ ...btn('#15803d'), marginTop: 10 }}
              >
                View Updated Dashboard →
              </button>
            </div>
          )}

          {importError && (
            <div style={{
              marginTop: 14, padding: 12, background: '#fee2e2',
              border: '1px solid #fca5a5', borderRadius: 8
            }}>
              <div style={{ fontWeight: 700, color: '#b91c1c', marginBottom: 6 }}>
                ❌ Import Failed
              </div>
              <div style={{ fontSize: '.82rem', color: '#991b1b' }}>
                {importError.error}
              </div>
              {importError.stderr && (
                <pre style={{
                  maxHeight: 200, overflow: 'auto', fontSize: '.7rem',
                  background: '#fff', padding: 8, borderRadius: 6, marginTop: 8,
                  whiteSpace: 'pre-wrap'
                }}>
                  {importError.stderr}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
